import { useRef, useEffect, useState } from 'react';

import Highcharts from 'highcharts/esm/highcharts.js';
import HighchartsReact from 'highcharts-react-official';

import 'highcharts/esm/modules/sankey.js';
import 'highcharts/esm/modules/organization.js';
import 'highcharts/esm/modules/accessibility.js';

import s from './OrganizationChart.module.scss';

import ExportSelect from '../ExportSelect/ExportSelect';
import AnimatedLogo from '../AnimatedLogo/AnimatedLogo';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ENTER_ANIMATION_DURATION = 1000; // 1 second

const NODE_WIDTH = 300;
const NODE_HEIGHT = 195; // Match actual card height
// const CARD_HEIGHT = 215; // Actual CSS card height

const OrganizationChart = ({
    organizationChart = [],
    isLoading = false,
    urlData = undefined,
    setOrgChartHasLoaded,
    selectedCompany = undefined,
}) => {
    const chartRef = useRef(null);
    const shadowChartRef = useRef(null);
    const chartContainerRef = useRef(null); // 👈 new
    const [isReady, setIsReady] = useState(false);
    const currentCompany = urlData?.holding;

    // Calculate the maximum nodes in any column/level
    const calculateMaxNodesPerLevel = (data) => {
        if (!data || data.length === 0) return 1;

        // Group nodes by their level
        const levelCounts = {};

        // Find root nodes (no parent)
        const rootNodes = data.filter((item) => !item.parent);

        // If no root nodes, treat first item as root
        if (rootNodes.length === 0 && data.length > 0) {
            levelCounts[0] = 1;
        } else {
            levelCounts[0] = rootNodes.length;
        }

        // Build parent-child relationships
        const parentToChildren = {};
        data.forEach((item) => {
            if (item.parent) {
                if (!parentToChildren[item.parent]) {
                    parentToChildren[item.parent] = [];
                }
                parentToChildren[item.parent].push(item);
            }
        });

        // Recursively count nodes at each level
        const countLevels = (nodeId, currentLevel) => {
            const children = parentToChildren[nodeId] || [];

            if (children.length > 0) {
                const nextLevel = currentLevel + 1;
                levelCounts[nextLevel] =
                    (levelCounts[nextLevel] || 0) + children.length;

                children.forEach((child) => {
                    countLevels(child.id, nextLevel);
                });
            }
        };

        // Start counting from root nodes
        rootNodes.forEach((root) => {
            countLevels(root.id, 0);
        });

        // Return the maximum count across all levels
        return Math.max(...Object.values(levelCounts));
    };

    const maxNodesInColumn = calculateMaxNodesPerLevel(organizationChart);

    // Calculate the maximum depth/number of columns
    const calculateMaxDepth = (data) => {
        if (!data || data.length === 0) return 1;

        // Build parent-child relationships
        const parentToChildren = {};
        data.forEach((item) => {
            if (item.parent) {
                if (!parentToChildren[item.parent]) {
                    parentToChildren[item.parent] = [];
                }
                parentToChildren[item.parent].push(item);
            }
        });

        // Find root nodes (no parent)
        const rootNodes = data.filter((item) => !item.parent);

        if (rootNodes.length === 0) {
            return 1; // If no clear hierarchy, assume single column
        }

        // Recursively find the maximum depth
        const findDepth = (nodeId, currentDepth) => {
            const children = parentToChildren[nodeId] || [];

            if (children.length === 0) {
                return currentDepth;
            }

            const childDepths = children.map((child) =>
                findDepth(child.id, currentDepth + 1)
            );

            return Math.max(...childDepths);
        };

        // Find the maximum depth starting from any root node
        const depths = rootNodes.map((root) => findDepth(root.id, 1));
        return Math.max(...depths);
    };

    const numberOfColumns = calculateMaxDepth(organizationChart);
    const dynamicHeight = Math.max(2, maxNodesInColumn) * (NODE_HEIGHT + 15);
    const dynamicWidth = numberOfColumns * (NODE_WIDTH + 150);

    // Calculate dynamic height for export (with smaller nodes)
    const getExportHeight = () => {
        const exportNodeHeight = NODE_HEIGHT / 2 + 10; // Add 10px to prevent cutoff
        return Math.max(2, numberOfColumns) * (exportNodeHeight + 15);
    };

    useEffect(() => {
        const loadModules = async () => {
            try {
                if (typeof setOrgChartHasLoaded === 'function') {
                    setOrgChartHasLoaded(true);
                }
                setIsReady(true);
            } catch (error) {
                if (typeof setOrgChartHasLoaded === 'function') {
                    setOrgChartHasLoaded(true);
                }
                console.error('Error loading Highcharts modules:', error);
                // Fallback: try to render without modules (might work for basic charts)
                setIsReady(true);
            }
        };

        loadModules();
    }, []);

    const movePercentElements = () => {
        const trackers = document.querySelectorAll(
            '.highcharts-label.highcharts-data-label.highcharts-data-label-color-undefined.highcharts-tracker'
        );

        trackers.forEach((tracker) => {
            const percentElement = tracker.querySelector(`.${s.Percent}`);
            if (percentElement) {
                // Remove from current position and insert as first child
                percentElement.remove();
                tracker.insertBefore(percentElement, tracker.firstChild);
            }
        });
    };

    const moveExportPercentElements = () => {
        const trackers = document.querySelectorAll(
            '.highcharts-label.highcharts-data-label.highcharts-data-label-color-undefined.highcharts-tracker'
        );

        trackers.forEach((tracker) => {
            const percentElement = tracker.querySelector(`.${s.PercentExport}`);
            if (percentElement) {
                // Remove from current position and insert as first child for export
                percentElement.remove();
                tracker.insertBefore(percentElement, tracker.firstChild);
            }
        });
    };

    const addLinkAnnotations = (chart) => {
        return;
        if (chart?.customAnnoElements?.length) {
            chart.customAnnoElements.forEach((elem) => {
                if (elem?.items?.length) {
                    elem.items.forEach((item) => {
                        if (item && item.destroy) {
                            item.destroy();
                        }
                    });
                }
            });
        }

        chart.customAnnoElements = [];

        const itemsWithAnnotations = organizationChart.filter(
            (item) => !!item.annotationOnLinkToParent
        );

        itemsWithAnnotations.forEach((item) => {
            const boxWidth = 100;
            const boxHeight = 40;

            const maxLabelLen = 13;

            if (!chart.series) {
                return;
            }

            const node = chart.series[0].nodes.find((n) => n.id === item.id);

            const parentNode = chart.series[0].nodes.find(
                (n) => n.id === item.parent
            );

            if (!node || !parentNode) {
                return;
            }

            const bboxNode = node.graphic.getBBox();

            // centered between the two nodes //

            const link = node.linksTo[0];
            if (!link || !link.graphic || !link.graphic.pathArray) {
                return;
            }

            const path = link.graphic.pathArray;

            if (path.length < 2) {
                return;
            }

            const lastPoint = path[path.length - 1];
            const penultimatePoint = path[path.length - 2];

            const lastX = lastPoint[1];
            const lastY = lastPoint[2];

            const penultimateX = penultimatePoint[1];
            const penultimateY = penultimatePoint[2];

            const t = 0.2; // 0 = one step before child, 1 = at child
            const finalX = penultimateX + t * (lastX - penultimateX);
            const finalY = penultimateY + t * (lastY - penultimateY);

            const label = item.annotationOnLinkToParent.text ?? '';
            if (!label) {
                return;
            }

            // text box
            let rect = chart.renderer
                .rect(
                    finalX - boxWidth / 2,
                    finalY - boxHeight / 2,
                    boxWidth,
                    boxHeight,
                    4 // rounded corners
                )
                .attr({
                    fill: '#ffffff',
                    zIndex: 5,
                })
                .add();
            const labelTruncated =
                label.length > maxLabelLen
                    ? label.slice(0, maxLabelLen) + '...'
                    : label;

            let txt = chart.renderer
                .text(
                    labelTruncated,
                    finalX,
                    finalY + 5 // nudge down so it's truly centered
                )
                .attr({
                    'text-anchor': 'middle',
                    color: 'black',
                    zIndex: 10,
                    style: 'font-size: 12px; font-family: var(--font-source-sans-3), sans-serif; color: black;',
                })
                .add();

            if (!!txt && !!txt.textStr) {
                chart.customAnnoElements.push({
                    items: [rect, txt],
                    id: item.id,
                    parentId: item.parent,
                });
            }
        });
    };

    const getSeriesData = (isExport = false) => {
        if (isExport) {
            // For export, create minimal series data with no link properties
            return organizationChart
                .filter((item) => item.parent)
                .map((item) => ({
                    from: item.parent,
                    to: item.id,
                }));
        }

        // Normal series data with colors and properties
        return organizationChart
            .filter((item) => item.parent)
            .map((item) => {
                const parentItem = organizationChart.find(
                    (parent) => parent.id === item.parent
                );

                if (parentItem && parentItem.childEdgeColor) {
                    return {
                        from: item.parent,
                        to: item.id,
                        link: {
                            color: parentItem.childEdgeColor,
                        },
                        linkOpacity: 1,
                    };
                }

                return {
                    from: item.parent,
                    to: item.id,
                };
            });
    };

    const truncateText = (text, maxLength = 30) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength - 3) + '...';
    };

    const getChartOptions = (isExport = false) => {
        const chartHeight = isExport ? getExportHeight() : dynamicHeight;
        const nodeHeight = isExport ? NODE_HEIGHT / 2 + 10 : NODE_HEIGHT; // Add 10px for export to prevent cutoff

        return {
            chart: {
                type: 'organization',
                backgroundColor: '#ececec',
                // marginTop: calculateMarginTop(),
                height: chartHeight,
                width: dynamicWidth,
                events: isExport
                    ? {
                          load: function () {
                              setTimeout(() => {
                                  moveExportPercentElements();
                              }, 100);
                          },
                          redraw: function () {
                              moveExportPercentElements();
                          },
                      }
                    : {
                          load: function () {
                              let chart = this;
                              setTimeout(() => {
                                  addLinkAnnotations(chart);
                                  movePercentElements();
                              }, ENTER_ANIMATION_DURATION);
                          },
                          redraw: function () {
                              const chart = this;
                              addLinkAnnotations(chart);
                              movePercentElements();
                          },
                      },
            },
            title: {
                text: '',
            },
            accessibility: {
                point: {
                    descriptionFormatter: function (point) {
                        return point.name + ', ' + point.title + '.';
                    },
                },
            },
            series: [
                {
                    type: 'organization',
                    name: 'Organization Chart',
                    keys: ['from', 'to'],
                    data: getSeriesData(isExport),
                    linkOpacity: 1,
                    levels: [
                        {
                            level: 0,
                            color: 'transparent',
                            dataLabels: {
                                color: '#333',
                            },
                        },
                        {
                            level: 1,
                            color: 'transparent',
                            dataLabels: {
                                color: '#333',
                            },
                        },
                        {
                            level: 2,
                            color: 'transparent',
                            dataLabels: {
                                color: '#333',
                            },
                        },
                    ],
                    nodes: organizationChart
                        .filter(
                            (item, index, self) =>
                                index ===
                                self.findIndex((t) => t.id === item.id)
                        )
                        .map((item) => ({
                            id: item.id,
                            name: item.name,
                            title: item.title,
                            revenue: item.revenue,
                            employees: item.employees,
                            incorporated: item.incorporated,
                            image: item.image,
                            status: item.status,
                            height: nodeHeight,
                            events: isExport
                                ? {}
                                : {
                                      mouseOver: function () {
                                          // Keep all annotations visible - no opacity changes
                                      },
                                      mouseOut: function () {
                                          // Keep all annotations visible - no opacity changes
                                      },
                                  },
                            dataLabels: {
                                useHTML: true,
                                format: !!item.personId
                                    ? `
                        <div class="${s.Card} ${s.PersonCard} ${currentCompany === item.id ? s.CurrentCompany : ''}">
                            <div class="${s.Wrap}">
                                <div class="${s.PersonImageWrap}">
                                    ${item.image ? `<img class="${s.PersonImage}" src="${item.image.src}" alt="" />` : ''}
                                </div>
                                <div class="${s.HeaderAndDetails}">
                                    <div class="${s.Header}">
                                        <h3 class="${s.Title}">${isExport ? truncateText(item.name, 35) : item.name}</h3>
                                        ${
                                            !item.status
                                                ? ''
                                                : `
                                            <div class="${s.OwnershipIcon}"></div>
                                        `
                                        }
                                    </div>
                                    <div class="${s.Details}">
                                        <div class="${s.PersonKeyRole}">${item.keyRole ?? ''}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="${s.Footer}">
                                ${item.personUrl ? `<a href="${item.personUrl}" class="${s.Link}">Individual page</a>` : ''}
                            </div>
                        </div>
                        `
                                    : `
                        <div class="${s.Card} ${currentCompany === item.id ? s.CurrentCompany : ''}">
                            ${
                                item.annotationOnLinkToParent?.text
                                    ? `<div class="${isExport ? s.PercentExport : s.Percent}">
                                        ${item.annotationOnLinkToParent.text.replace('.0%', '%')}
                                    </div>`
                                    : ''
                            }
                            <div class="${s.Header}">
                                <h3 class="${s.Title}">${isExport ? truncateText(item.name, 35) : item.name}</h3>
                                ${
                                    !item.status
                                        ? ''
                                        : `
                                    <div class="${s.OwnershipIcon}"></div>
                                `
                                }
                            </div>
                            <div class="${s.Details}">
                                <div>
                                    ${item.revenue ? `<div>Revenue: ${+(item.revenue / 1e6).toFixed(0)} mUSD</div>` : ''}
                                    ${item.employees ? `<div>Employees: ${item.employees}</div>` : ''}
                                    ${item.incorporated ? `<div>Incorporated: ${item.incorporated}</div>` : ''}
                                </div>
                                <div class="${s.Avatars}">
                                    ${
                                        item.relatedIndividuals &&
                                        item.relatedIndividuals.length > 0
                                            ? item.relatedIndividuals
                                                  .filter(
                                                      (individual) =>
                                                          individual.src
                                                              ?.useAvatar ||
                                                          individual.src
                                                  )
                                                  .slice(0, 5)
                                                  .map((individual, index) => {
                                                      const isAvatar =
                                                          individual.src
                                                              ?.useAvatar;
                                                      const src = isAvatar
                                                          ? '/img/person.svg'
                                                          : individual.src;
                                                      const className = isAvatar
                                                          ? ` class="${s.Avatar}"`
                                                          : '';
                                                      return `<img src="${src}" alt=""${className} />`;
                                                  })
                                                  .join('')
                                            : ''
                                    }
                                </div>
                            </div>
                            ${
                                isExport
                                    ? ''
                                    : `<div class="${s.Footer}">
                                ${
                                    item.companyUrl
                                        ? `<a href="${item.companyUrl}" class="${s.Link}">Company page</a>`
                                        : ''
                                }
                                ${
                                    (
                                        item.type === 'Person'
                                            ? !!item.personUrl
                                            : item.relatedIndividuals?.length >
                                                  0 &&
                                              (currentCompany === item.id ||
                                                  !!item.personUrl)
                                    )
                                        ? `<a href="${
                                              currentCompany === item.id
                                                  ? '#related-individuals'
                                                  : item.personUrl
                                          }" class="${s.Link}">${
                                              item.type === 'Person'
                                                  ? 'View profile'
                                                  : 'Related individuals'
                                          }</a>`
                                        : ''
                                }
                            </div>`
                            }
                        </div>
                        `,
                                style: {
                                    fontWeight: 'normal',
                                },
                            },
                        })),
                    // use this for non-intersecting links between nodes
                    // link: {
                    //     type: 'curved',
                    //     offset: 0.9,
                    //     lineWidth: 3
                    // },
                    link: {
                        states: {
                            hover: {
                                opacity: 1,
                            },
                            inactive: {
                                opacity: 1,
                            },
                        },
                    },
                    colorByPoint: false,
                    color: '#4A90E2',
                    dataLabels: {
                        color: '#333',
                    },
                    borderColor: 'transparent',
                    nodeWidth: NODE_WIDTH, //300,
                    nodeHeight: nodeHeight, //450,
                },
            ],
            tooltip: {
                enabled: false,
            },
            exporting: {
                allowHTML: true,
                sourceWidth: 800,
                sourceHeight: 600,
            },
            plotOptions: {
                organization: {
                    hangingIndent: organizationChart.length <= 3 ? 15 : 20,
                    borderColor: 'transparent',
                    borderWidth: 0,
                    levelIsConstant: false, // Allow flexible spacing
                    levelOffset: 250, // Increase vertical spacing between levels
                    animation: isExport ? false : true, // Disable animations for export
                    dataLabels: {
                        nodeFormatter: function () {
                            return this.point.name;
                        },
                    },
                    states: {
                        hover: {
                            brightness: 0,
                            opacity: 1,
                            linkOpacity: 1,
                        },
                        inactive: {
                            opacity: 1,
                            linkOpacity: 1,
                        },
                    },
                    link: {
                        opacity: 1,
                        states: {
                            hover: {
                                opacity: 1,
                            },
                            inactive: {
                                opacity: 1,
                            },
                        },
                    },
                },
            },
        };
    };

    const chartOptions = getChartOptions();

    const handleExport = async (format = 'pdf') => {
        const shadowChartContainer =
            shadowChartRef.current?.container?.current?.parentElement;
        const shadowChartEl =
            shadowChartRef.current?.container?.current?.querySelector(
                '.highcharts-container'
            );
        const mainChartContainer = chartContainerRef.current;
        if (!shadowChartEl || !shadowChartContainer || !mainChartContainer)
            return;

        // Keep main chart visible, just position shadow chart off-screen for capture
        const originalShadowStyle = shadowChartContainer.style.cssText;
        shadowChartContainer.style.cssText = `
            position: fixed !important;
            left: -9999px !important;
            top: -9999px !important;
            visibility: visible !important;
            z-index: -1 !important;
            background: white !important;
            opacity: 1 !important;
            pointer-events: none !important;
        `;

        // Wait much longer for complete rendering and isolation
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Manually trigger percentage element repositioning for export
        moveExportPercentElements();

        // Get the actual SVG bounds to crop to content
        const svgElement = shadowChartEl.querySelector('svg');
        if (!svgElement) return;

        const bbox = svgElement.getBBox();

        const contentX = bbox.x;
        const contentY = bbox.y;
        const contentWidth = bbox.width;
        const contentHeight = bbox.height;

        const padding = 30;

        const exportX = contentX - padding;
        const exportY = contentY - padding;
        const exportWidth = contentWidth + padding * 2;
        const exportHeight = contentHeight + padding * 2;

        const canvas = await html2canvas(shadowChartEl, {
            useCORS: true,
            scale: 2,
            x: exportX,
            y: exportY,
            width: exportWidth,
            height: exportHeight,
            windowWidth: shadowChartEl.scrollWidth,
            windowHeight: shadowChartEl.scrollHeight,
            backgroundColor: null, // transparent bakgrund
        });

        // Restore original shadow chart style
        shadowChartContainer.style.cssText = originalShadowStyle;

        const fileName = `${selectedCompany?.name || 'organization-chart'}.${format}`;

        if (format === 'pdf') {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'pt',
                format: [canvas.width, canvas.height],
            });
            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(fileName);
        } else if (format === 'webp') {
            const imgData = canvas.toDataURL('image/webp', 0.85);
            const link = document.createElement('a');
            link.href = imgData;
            link.download = fileName;
            link.click();
        }
    };

    useEffect(() => {
        const handleAnchorClick = (e) => {
            const target = e.target.closest('a[href^="#"]');
            if (!target) return;

            const hash = target.getAttribute('href');

            if (hash === '#related-individuals') {
                e.preventDefault();
                const el = document.querySelector(hash);
                if (el) {
                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            }
        };

        document.addEventListener('click', handleAnchorClick);

        return () => {
            document.removeEventListener('click', handleAnchorClick);
        };
    }, []);

    return (
        <div className={s.Root}>
            {isReady && !isLoading ? (
                <div className={s.ChartContainer} ref={chartContainerRef}>
                    <h2 className={s.SelectedCompanyName}>
                        {selectedCompany?.name}
                    </h2>
                    <div className={s.Toolbar}>
                        <ExportSelect
                            onPdf={() => handleExport('pdf')}
                            onImage={() => handleExport('webp')}
                        />
                        {/* <Button
                            modifier="Secondary"
                            title="Export as PDF"
                            className={s.ExportButton}
                            onClick={() => handleExport('pdf')}
                        />
                        <Button
                            modifier="Secondary"
                            title="Export as image"
                            className={s.ExportButton}
                            onClick={() => handleExport('webp')}
                        /> */}
                    </div>
                    <div className={s.HighchartsWrapper}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={chartOptions}
                            ref={chartRef}
                        />
                    </div>
                    {/* Hidden shadow chart for exports */}
                    <div
                        style={{
                            position: 'absolute',
                            left: '-9999px',
                            top: '-9999px',
                            visibility: 'hidden',
                        }}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={getChartOptions(true)}
                            ref={shadowChartRef}
                        />
                    </div>
                    {/* <LegendCard /> */}
                </div>
            ) : (
                <div className={s.Loading}>
                    <div className={s.LoadingIcon}>
                        <AnimatedLogo width={29} height={34} stroke="#616161" />
                    </div>
                    <div className={s.LoadingText}>
                        Almost there! Finalizing your data view…
                    </div>
                </div>
            )}
        </div>
    );
};

OrganizationChart.propTypes = {};

export default OrganizationChart;
